import { type AgreeToTermsPolicy } from '@logto/schemas';
import classNames from 'classnames';
import { type TFuncKey } from 'i18next';
import { useContext, useMemo, type ReactNode } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import BrandingHeader from '@/components/BrandingHeader';
import type { Props as TextLinkProps } from '@/components/TextLink';
import TextLink from '@/components/TextLink';
import TermsAndPrivacyLinks from '@/containers/TermsAndPrivacyLinks';
import useTerms from '@/hooks/use-terms';
import type { Props as PageMetaProps } from '@/shared/components/PageMeta';
import { getBrandingLogoUrl } from '@/shared/utils/logo';
import { layoutClassNames } from '@/utils/consts';

import FirstScreenLayout from '../FirstScreenLayout';

type Props = {
  readonly children: ReactNode;
  readonly pageMeta: PageMetaProps;
  readonly title: TFuncKey;
  readonly description: string;
  readonly footerTermsDisplayPolicies?: AgreeToTermsPolicy[];
  readonly authOptionsLink: TextLinkProps;
};

/** First screen of a single auth method: identifier sign-in / register and the SSO landing. */
const FocusedAuthPageLayout = ({
  children,
  pageMeta,
  title,
  description,
  footerTermsDisplayPolicies = [],
  authOptionsLink,
}: Props) => {
  const { agreeToTermsPolicy } = useTerms();
  const { experienceSettings, theme } = useContext(PageContext);

  const shouldDisplayFooterTerms = useMemo(
    () => agreeToTermsPolicy && footerTermsDisplayPolicies.includes(agreeToTermsPolicy),
    [agreeToTermsPolicy, footerTermsDisplayPolicies]
  );

  const logo = experienceSettings
    ? getBrandingLogoUrl({
        theme,
        branding: experienceSettings.branding,
        isDarkModeEnabled: experienceSettings.color.isDarkModeEnabled,
      })
    : undefined;

  return (
    <FirstScreenLayout pageMeta={pageMeta}>
      <BrandingHeader
        className={classNames('page-header mb-6', layoutClassNames.brandingHeader)}
        headline={title}
        description={description}
        logo={logo}
      />
      {children}
      <div className="mt-6 flex flex-col items-center gap-4 text-center">
        <TextLink {...authOptionsLink} />
        {shouldDisplayFooterTerms && <TermsAndPrivacyLinks className="text-xs text-muted" />}
      </div>
    </FirstScreenLayout>
  );
};

export default FocusedAuthPageLayout;
